"use client"

import { useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type { Verdict } from "@/components/search/graphite-search-results"
import {
  getGetSearchResultsHashQueryKey,
  useGetJobs,
  useGetSearch,
  usePatchJobsJobIdAssociateSearch,
  usePostJobsJobIdPipelineAdd,
} from "openapi/api/endpoints"
import type { Job } from "openapi/api/model"
import { asArray } from "@/lib/as-array"
import type { CandidateAction } from "@/hooks/use-candidate-actions"

/**
 * What a Yes, a Maybe and a hide actually do to a search result.
 *
 * All of this lived inside the result card, which meant every row mounted its
 * own copy of the jobs query, the two mutations and the link dialog — thirty
 * rows, thirty of each. It also meant the row could not be a presentational
 * component, and 27a's row is drawn from data alone.
 *
 * A Yes is not a flag: it puts the candidate on the job this search feeds, at
 * longlist. Where no job is linked yet the link dialog opens first, because
 * there is nowhere for the Yes to land.
 */
export function useResultVerdicts({
  searchHash,
  searchId,
  setAction,
  getAction,
  addedIds,
}: {
  searchHash: string
  /**
   * The search's own id, which `/search/queries` already gave the page. Passed
   * in rather than only looked up from the saved-search list: that list can
   * fail or simply not contain the hash, and an unidentified search must not
   * read as "a search with no job linked".
   */
  searchId?: string
  setAction: (candidateId: string, action: CandidateAction) => void
  getAction: (candidateId: string) => CandidateAction
  /** Candidates the pipeline already holds, from the page's own jobs query. */
  addedIds: Set<string>
}) {
  const queryClient = useQueryClient()
  const [pendingCandidateId, setPendingCandidateId] = useState<string | null>(null)
  const [optimisticallyAdded, setOptimisticallyAdded] = useState<Set<string>>(
    () => new Set(),
  )

  const { data: searches } = useGetSearch()
  const currentSearchId =
    searchId ??
    (Array.isArray(searches)
      ? searches.find((search) => search.normalized_query_hash === searchHash)?.id
      : undefined)

  /*
    The authoritative lookup, not a scan of the first page. `/jobs` is
    paginated, and its own parameter doc says `limit` and `offset` are "ignored
    if job_id or associated_search_id is provided" — so `searchId` answers this
    exact question. Scanning a bare `/jobs` meant an org with more than a page
    of jobs could be told its search had none, and offered to link a second one.
  */
  const {
    data: linkedJobs,
    isPending: isPendingJobs,
    isError: isJobsError,
  } = useGetJobs(
    { searchId: currentSearchId as string },
    { query: { enabled: Boolean(currentSearchId) } },
  )

  const linkedJob = asArray<Job>(
    Array.isArray(linkedJobs) ? linkedJobs : linkedJobs ? [linkedJobs] : [],
  )[0]

  /*
    A failed lookup is unknown, not empty. Reading an error as "no job linked"
    would put the one-way link control in front of someone on the strength of a
    request that never answered.
  */
  /*
    Unresolved covers three states, not one: the search is not identified yet,
    the job lookup has not answered, or it failed. All three mean "we do not
    know whether a job is linked", and the link is one-way, so all three have to
    withhold the control. Only a completed lookup on a known search says none.
  */
  const isLoadingJobs = !currentSearchId || isPendingJobs || isJobsError

  const { mutateAsync: addToPipeline } = usePostJobsJobIdPipelineAdd()
  /*
    No optimistic write here any more. The lookup is a query keyed by searchId
    now, and guessing its next answer from the job list is how the two could
    disagree; invalidating on settle keeps one source of truth.
  */
  const { mutateAsync: linkJob } = usePatchJobsJobIdAssociateSearch()

  const refreshResults = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: getGetSearchResultsHashQueryKey(searchHash),
      exact: false,
    })
  }, [queryClient, searchHash])

  const rollback = useCallback((candidateId: string) => {
    setOptimisticallyAdded((previous) => {
      const next = new Set(previous)
      next.delete(candidateId)
      return next
    })
  }, [])

  const addCandidate = useCallback(
    async (candidateId: string, jobId: string) => {
      setOptimisticallyAdded((previous) => new Set(previous).add(candidateId))
      try {
        const response = (await addToPipeline({
          jobId,
          data: { candidateIds: [candidateId], stage: "longlist" },
        })) as { failures?: { candidateId?: string; error?: string }[] } | undefined

        /*
          The endpoint answers 207 as well as 201: a request can resolve with
          the candidate listed under `failures` and nothing thrown. Treating any
          resolved response as success left the row saying "Added" about a
          candidate the job never received.
        */
        const failure = response?.failures?.find(
          (entry) => String(entry?.candidateId) === String(candidateId),
        )
        if (failure) {
          rollback(candidateId)
          toast.error("The job did not take that candidate", {
            description: failure.error ?? "The pipeline reported a failure.",
          })
          return false
        }

        await queryClient.invalidateQueries({ queryKey: ["/jobs"], exact: false })
        return true
      } catch (error) {
        // Put the row back the way it was, or it reads as added forever.
        rollback(candidateId)
        toast.error("Could not add the candidate", { description: `${error}` })
        return false
      }
    },
    [addToPipeline, queryClient, rollback],
  )

  const setVerdict = useCallback(
    async (candidateId: string, verdict: Verdict) => {
      if (verdict === "yes") {
        /*
          Until both lookups have answered, "no linked job" is a guess, and
          acting on it opens the link dialog — which would attach a second job
          to a search that may already have one. The association is one-way, so
          a Yes waits rather than guesses.
        */
        if (isLoadingJobs) return

        if (linkedJob?.id) {
          await addCandidate(candidateId, linkedJob.id)
        } else {
          // Nowhere for the Yes to land yet — pick the job first.
          setPendingCandidateId(candidateId)
        }
        return
      }

      /*
        A hide or a maybe on a candidate already on the job is a verdict about
        the *result*, not a retraction of the add — nothing in the API removes a
        candidate from a pipeline, so nothing here pretends to.
      */

      // `null` is how the actions store spells "no verdict".
      setAction(candidateId, verdict === "none" ? null : verdict)
      await refreshResults()
    },
    [addCandidate, isLoadingJobs, linkedJob?.id, refreshResults, setAction],
  )

  /** The link dialog's own submit: attach the job, then land the waiting Yes. */
  const linkJobAndAdd = useCallback(
    async (jobId: string) => {
      if (!currentSearchId) return
      try {
        await linkJob({ jobId, data: { searchId: currentSearchId } })
        await queryClient.invalidateQueries({ queryKey: ["/jobs"], exact: false })
        if (pendingCandidateId) await addCandidate(pendingCandidateId, jobId)
      } catch (error) {
        toast.error("Could not link the job", { description: `${error}` })
      } finally {
        setPendingCandidateId(null)
      }
    },
    [addCandidate, currentSearchId, linkJob, pendingCandidateId, queryClient],
  )

  const verdictOf = useCallback(
    (candidateId: string): Verdict => {
      const action = getAction(candidateId)
      return action === "maybe" || action === "hidden" ? action : "none"
    },
    [getAction],
  )

  return {
    linkedJob,
    /* Until both queries have answered, whether a job is linked is unknown —
       and linking is one-way, so an unknown must not read as "none". */
    isResolvingLink: isLoadingJobs,
    currentSearchId,
    verdictOf,
    setVerdict,
    /** On the job already, or on its way there. */
    addedOrOptimistic: new Set([...addedIds, ...optimisticallyAdded]),
    pendingCandidateId,
    closeLinkDialog: () => setPendingCandidateId(null),
    linkJobAndAdd,
  }
}
